'use client';

import { useCallback, useEffect, useState } from 'react';
import { appEnv, isLocalEnv } from '@/config/config';

/**
 * Dev-tool visibility flags.
 *
 * Unset key → environment default.
 * `1` / `true` → force on. `0` / `false` → force off.
 *
 * Master key `sb:dev-tools` applies when a per-tool key is unset.
 *
 * Console:
 *   localStorage.setItem('sb:dev-tools', '1')
 *   localStorage.setItem('sb:dev:network', '0')
 *   window.dispatchEvent(new Event('sb:dev-tools'))
 */
export const DEV_TOOL_EVENT = 'sb:dev-tools';
export const OPEN_QA_INSPECTOR_EVENT = 'sb:open-qa-inspector';
export const QA_INSPECTOR_STATE_EVENT = 'sb:qa-inspector-state';

export const DEV_TOOL_KEYS = {
  master: 'sb:dev-tools',
  network: 'sb:dev:network',
  searchRanking: 'sb:dev:search-ranking',
  commit: 'sb:show-commit'
} as const;

export type DevToolKey = (typeof DEV_TOOL_KEYS)[keyof typeof DEV_TOOL_KEYS];

export type DevToolFlagDef = {
  key: DevToolKey;
  title: string;
  description: string;
  /** When false, unset means “no master override”, not the chrome default. */
  usesChromeDefault: boolean;
};

export const DEV_TOOL_FLAG_CATALOG: DevToolFlagDef[] = [
  {
    key: DEV_TOOL_KEYS.master,
    title: 'All developer tools',
    description:
      'Master override for this browser. When on, every tool below is shown unless that tool has its own off override. When off, they are hidden unless a tool is forced on. Unset uses each tool’s environment default.',
    usesChromeDefault: false
  },
  {
    key: DEV_TOOL_KEYS.network,
    title: 'Network telemetry',
    description:
      'Shows the Network row in the corner dock and captures client request logs. Open the inspector from the dock to inspect traffic.',
    usesChromeDefault: true
  },
  {
    key: DEV_TOOL_KEYS.searchRanking,
    title: 'Search ranking debug',
    description:
      'Shows the Search ranking debug row in the dock. Turning that row on replaces program cards with ranking debug cards.',
    usesChromeDefault: true
  },
  {
    key: DEV_TOOL_KEYS.commit,
    title: 'Deployed commit',
    description:
      'Shows the commit SHA and app environment in the dock so you can confirm which build this browser is serving. Hover for the full SHA; click to copy.',
    usesChromeDefault: true
  }
];

function parseFlag(value: string | null): boolean | null {
  if (value == null || value === '') return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === '1' || normalized === 'true') return true;
  if (normalized === '0' || normalized === 'false') return false;
  return null;
}

function readStorage(key: string): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    return parseFlag(localStorage.getItem(key));
  } catch {
    return null;
  }
}

/**
 * Default ON everywhere except the live production portal (`APP_ENV=production`
 * and not local). `NODE_ENV=production` / `RELEASE_ENV=true` do not hide tools.
 *
 * Hostname is applied in `isDevToolEnabled` after mount so SSR/hydration stay aligned.
 */
export function defaultDevChromeEnabled(): boolean {
  if (isLocalEnv) return true;
  return appEnv?.toLowerCase() !== 'production';
}

function hostIsLocal(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
}

export function isDevToolEnabled(
  key: DevToolKey,
  envDefault: boolean
): boolean {
  const perTool = readStorage(key);
  if (perTool !== null) return perTool;

  const master = readStorage(DEV_TOOL_KEYS.master);
  if (master !== null) return master;

  return envDefault || hostIsLocal();
}

export function useDevToolFlag(key: DevToolKey, envDefault: boolean): boolean {
  const [enabled, setEnabled] = useState(envDefault);

  const sync = useCallback(() => {
    setEnabled(isDevToolEnabled(key, envDefault));
  }, [key, envDefault]);

  useEffect(() => {
    sync();
    window.addEventListener(DEV_TOOL_EVENT, sync);
    window.addEventListener('storage', sync);
    const timer = window.setInterval(sync, 1000);
    return () => {
      window.removeEventListener(DEV_TOOL_EVENT, sync);
      window.removeEventListener('storage', sync);
      window.clearInterval(timer);
    };
  }, [sync]);

  return enabled;
}

export type FlagOverride = boolean | null;

export function getDevToolOverride(key: DevToolKey): FlagOverride {
  return readStorage(key);
}

export function setDevToolOverride(key: DevToolKey, value: FlagOverride): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value ? '1' : '0');
  } catch {
    return;
  }
  window.dispatchEvent(new Event(DEV_TOOL_EVENT));
}

export function effectiveDevToolValue(def: DevToolFlagDef): boolean {
  if (def.usesChromeDefault) {
    return isDevToolEnabled(def.key, defaultDevChromeEnabled());
  }
  return getDevToolOverride(def.key) === true;
}
